import { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@verlo/shared/lib/utils';
import Empty from './Empty';
import Loading from './Loading';

/**
 * The one table (Back Office Review, wave A). Hairline rows, a sticky head, sortable columns,
 * numbers in mono and right-aligned, a row that is a tap target when it opens something, row
 * actions that show on hover so twenty rows don't carry eighty icons, an empty state built in.
 *
 *   <DataTable
 *     columns={[
 *       { key: 'name', label: 'Name', sortable: true, render: (r) => <b>{r.name}</b> },
 *       { key: 'rate', label: 'Rate', align: 'right', mono: true, render: (r) => `£${r.rate}/hr` },
 *     ]}
 *     rows={staff} rowKey="id" onRowClick={(r) => open(r)}
 *     actions={(r) => <Button size="sm" variant="ghost">Edit</Button>}
 *     empty={{ word: 'Staff', title: 'Nobody on the team yet' }}
 *   />
 *
 * `scroll`: a long table scrolls inside itself, so its heading row stays at the top while you
 * read down it (Items, Back office sweep Oct 2026). Without it, the sideways scroll a wide
 * table needs makes the page, not the table, scroll, and the heading goes with it.
 *
 * `rowTestId`: (row) => a test id for that row, for the specs that find a row by its record.
 */
const DataTable = ({ columns, rows, rowKey = 'id', onRowClick, actions, empty, loading, defaultSort, dense = false, scroll = false, rowTestId, className, testId = 'data-table' }) => {
  const [sort, setSort] = useState(defaultSort || null); // { key, dir }
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const get = col?.sortValue || ((r) => r[sort.key]);
    return [...rows].sort((a, b) => {
      const x = get(a); const y = get(b);
      const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggle = (key) => setSort((s) => (s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));

  if (loading) return <Loading shape="table" />;
  if (!rows.length && empty) return <Empty {...empty} />;

  const pad = dense ? 'px-3 py-2' : 'px-4 py-2.5';
  return (
    <div className={cn('overflow-x-auto rounded-lg border border-hairline bg-white', scroll && 'max-h-[calc(100vh-220px)] overflow-y-auto', className)} data-testid={testId}>
      <table className="w-full border-collapse text-[13.5px]">
        <thead className="sticky top-0 z-10 bg-surface-2">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn('border-b border-hairline font-heading text-[11px] font-bold uppercase tracking-[0.05em] text-ink-faint', pad, c.align === 'right' ? 'text-right' : 'text-left', c.sortable && 'select-none', c.className)}
                aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {c.sortable ? (
                  <button type="button" onClick={() => toggle(c.key)} className="inline-flex items-center gap-1 uppercase hover:text-ink">
                    {c.label}
                    {sort?.key === c.key ? (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-40" />}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">{c.label}</span>
                )}
              </th>
            ))}
            {actions && <th scope="col" className={cn('border-b border-hairline', pad)}><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={typeof rowKey === 'function' ? rowKey(r) : r[rowKey]}
              data-testid={rowTestId ? rowTestId(r) : undefined}
              className={cn('group border-b border-hairline last:border-0', onRowClick && 'cursor-pointer hover:bg-hover-tint focus-within:bg-surface-2')}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r); } } : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={cn('align-middle text-ink', pad, c.align === 'right' && 'text-right', c.mono && 'font-mono tabular-nums', c.muted && 'text-ink-soft', c.className)}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
              {actions && (
                <td className={cn('w-px whitespace-nowrap text-right', pad)} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">{actions(r)}</div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
